import { Component, EventEmitter, Inject, Output } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { InviteLinkDispatchType } from '../../../../enums/invite-link-dispatch-type';
import { InviteLinkService } from '../../../../api/services/invite-link.service';
import { InviteLinkModel } from '../../../../api/models/invite-link-model';
import { AuthService } from '../../../../services/auth.service';
import { ToastService } from '../../../../services/toast.service';
import { QuestionnaireService } from '../../../../api/services/questionnaire.service';
import { InviteLinkType } from '../../../../enums/invite-link-type';

@Component({
  selector: 'app-create-invite-link',
  templateUrl: './create-invite-link.component.html',
  standalone: false,
})
export class CreateInviteLinkComponent {
  /**
   * The type of dispatch selected
   */
  selectedInviteLinkDispatchType: InviteLinkDispatchType;

  /**
   * The type of invite selected
   */
  selectedInviteLinkType: InviteLinkType = InviteLinkType.Unique;

  /**
   * Recipients who will receive remote invite via email or SMS
   */
  remoteInviteRecipients: string[] = [];

  /**
   * Input value for new recipient
   */
  currentRecipientInput = '';

  /**
   * The questionnaire guid for which this invite is being created
   */
  questionnaireGuid!: string;

  /**
   * The generated invite link
   */
  inviteLink: InviteLinkModel | undefined;

  /**
   * The message info for remote invite dispatch
   */
  messageInfo = {
    subject: undefined,
    message: undefined,
  };

  /**
   * When the generated link(s) should expire
   */
  linkExpiryDate: string | null = null;

  /**
   * Whether invites are currently being dispatched
   */
  loading: boolean = false;

  /**
   * Whether the user has copied a link to their clipboard
   */
  copiedToClipboard: boolean = false;

  /**
    Event emitted when new invite links are created
  */
  @Output() inviteLinksCreatedEvent = new EventEmitter<InviteLinkModel[]>();

  constructor(
    public dialogRef: MatDialogRef<CreateInviteLinkComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      inviteLinkType: InviteLinkDispatchType;
      questionnaireGuid: string;
    },
    private inviteLinkService: InviteLinkService,
    private authService: AuthService,
    private questionnaireService: QuestionnaireService,
    private toastService: ToastService,
  ) {
    this.selectedInviteLinkDispatchType = data.inviteLinkType;
    this.questionnaireGuid = data.questionnaireGuid;
  }

  addRecipient() {
    this.remoteInviteRecipients.push(this.currentRecipientInput);
    this.currentRecipientInput = '';
  }

  onClickCopyToClipboard() {
    this.copiedToClipboard = true;
  }

  /**
   * Sends invite links via email or SMS
   */
  sendRemoteInviteLinks() {
    this.loading = true;

    this.questionnaireService
      .questionnairesSendInvitesPost$Response(
        {
          questionnaireGuid: this.questionnaireGuid,
          body: {
            recipients: this.remoteInviteRecipients,
            isPublicInvite:
              this.selectedInviteLinkType === InviteLinkType.Public,
            expiryDate: this.linkExpiryDate,
            message: this.messageInfo.message,
            subject: this.messageInfo.subject,
            dispatchType: this.selectedInviteLinkDispatchType,
          },
        },
        this.authService.getAuthorisationHeaders(),
      )
      .subscribe({
        next: (inviteLinkModels) => {
          this.loading = false;
          this.inviteLinksCreatedEvent.emit(inviteLinkModels.body);
          this.dialogRef.close();
          this.toastService.info('Successfully Sent Invites');
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  /**
   * Generates a bespoke invite link which can be used directly, shared via social media, or embedded into a webpage
   */
  generateBespokeLink() {
    this.loading = true;
    this.copiedToClipboard = false;

    this.inviteLinkService
      .inviteLinksPost(
        {
          body: {
            questionnaireGuid: this.questionnaireGuid,
            publicInvite: this.selectedInviteLinkType === InviteLinkType.Public,
            expiryDate: this.linkExpiryDate,
          },
        },
        this.authService.getAuthorisationHeaders(),
      )
      .subscribe({
        next: (inviteLinkModel) => {
          this.inviteLink = inviteLinkModel;
          this.loading = false;
          this.inviteLinksCreatedEvent.emit([inviteLinkModel]);
        },
        error: () => {
          this.toastService.error('Could Not Generate Invite Link');
          this.loading = false;
        },
      });
  }

  onDispatchTypeChanged() {
    this.remoteInviteRecipients = [];
    this.currentRecipientInput = '';
  }

  protected readonly InviteLinkType = InviteLinkType;
  protected readonly InviteLinkDispatchType = InviteLinkDispatchType;
}
